import React, { useState } from 'react';
import {
  Space,
  Card,
  Table,
  Tag,
  Typography,
  Button,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useCreditTransactions } from '../hooks/useCreditTransactions';
import {
  CreditTransaction,
  CreditTransactionType,
  CreditTransactionFilters,
} from '../../api/services/adminService';
import UserCreditSummaryModal from './UserCreditSummaryModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const TYPE_COLORS: Record<CreditTransactionType, string> = {
  PURCHASE: 'blue',
  SUBSCRIPTION_GRANT: 'green',
  AD_POST: 'orange',
  ADMIN_ADJUSTMENT: 'purple',
  REFUND: 'red',
};

const CreditTransactions: React.FC = () => {
  const { t } = useTranslation();
  const TYPE_LABELS: Record<CreditTransactionType, string> = {
    PURCHASE: t('admin.credit_transactions.type_purchase'),
    SUBSCRIPTION_GRANT: t('admin.credit_transactions.type_subscription'),
    AD_POST: t('admin.credit_transactions.type_ad_post'),
    ADMIN_ADJUSTMENT: t('admin.credit_transactions.type_admin_adj_short'),
    REFUND: t('admin.credit_transactions.type_refund'),
  };

  const { transactions, loading, pagination, filters, handleFilterChange, handleTableChange, refetch } =
    useCreditTransactions();

  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [summaryVisible, setSummaryVisible] = useState(false);

  const creditsIn = transactions.reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);
  const creditsOut = transactions.reduce((sum, t) => sum + (t.amount < 0 ? Math.abs(t.amount) : 0), 0);
  const uniqueUsers = new Set(transactions.map((t) => t.userId)).size;

  const applyFilters = (overrides: Partial<CreditTransactionFilters> = {}) => {
    handleFilterChange({ ...filters, ...overrides });
  };

  const resetFilters = () => {
    setUserSearch('');
    handleFilterChange({});
  };

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setSummaryVisible(true);
  };

  const columns: ColumnsType<CreditTransaction> = [
    {
      title: t('admin.credit_transactions.col_user'),
      key: 'user',
      render: (_: unknown, record: CreditTransaction) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', color: '#fe5a1d' }}
            onClick={() => handleUserClick(record.userId)}
          >
            {record.user?.firstName} {record.user?.lastName}
          </Button>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.user?.email}
          </Text>
        </Space>
      ),
    },
    {
      title: t('admin.credit_transactions.col_type'),
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: CreditTransactionType) => (
        <Tag color={TYPE_COLORS[type]}>{TYPE_LABELS[type]}</Tag>
      ),
    },
    {
      title: t('admin.credit_transactions.col_amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 90,
      align: 'right',
      render: (amount: number) => (
        <Text strong style={{ color: amount > 0 ? '#52c41a' : '#ff4d4f' }}>
          {amount > 0 ? `+${amount}` : amount}
        </Text>
      ),
    },
    {
      title: t('admin.credit_transactions.col_before'),
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      width: 80,
      align: 'right',
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t('admin.credit_transactions.col_after'),
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      width: 80,
      align: 'right',
    },
    {
      title: t('admin.credit_transactions.col_listing'),
      key: 'listing',
      render: (_: unknown, record: CreditTransaction) => {
        if (record.listing) {
          const { make, model, year } = record.listing;
          return (
            <Text style={{ fontSize: 12 }}>
              {[year, make, model].filter(Boolean).join(' ') || record.referenceId}
            </Text>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: t('admin.credit_transactions.col_reason'),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: t('admin.credit_transactions.col_date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('admin.credit_transactions.title')}
          </Title>
          <Text type="secondary">{t('admin.credit_transactions.subtitle')}</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading} className="admin-filter-btn">
          {t('admin.credit_transactions.btn_refresh')}
        </Button>
      </div>

      {/* Stats */}
      <Row gutter={16}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic title={t('admin.credit_transactions.stat_total')} value={pagination.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic
              title={t('admin.credit_transactions.stat_credits_in')}
              value={creditsIn}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic
              title={t('admin.credit_transactions.stat_credits_out')}
              value={creditsOut}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic title={t('admin.credit_transactions.stat_unique_users')} value={uniqueUsers} />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card bordered={false} className="admin-filter-card">
        <Row gutter={[12, 12]} align="middle">
          <Col flex="200px">
            <Input
              className="admin-filter-input"
              placeholder={t('admin.credit_transactions.search_placeholder')}
              prefix={<SearchOutlined />}
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              onPressEnter={() => applyFilters({ userId: userSearch.trim() || undefined })}
              allowClear
              onClear={() => applyFilters({ userId: undefined })}
            />
          </Col>
          <Col flex="180px">
            <Select
              className="admin-filter-select"
              style={{ width: '100%' }}
              placeholder={t('admin.credit_transactions.filter_all_types')}
              allowClear
              value={filters.type}
              onChange={(val) => applyFilters({ type: val as CreditTransactionType | undefined })}
              options={[
                { value: 'PURCHASE', label: t('admin.credit_transactions.type_purchase') },
                { value: 'SUBSCRIPTION_GRANT', label: t('admin.credit_transactions.type_subscription_grant') },
                { value: 'AD_POST', label: t('admin.credit_transactions.type_ad_post') },
                { value: 'ADMIN_ADJUSTMENT', label: t('admin.credit_transactions.type_admin_adjustment') },
                { value: 'REFUND', label: t('admin.credit_transactions.type_refund') },
              ]}
            />
          </Col>
          <Col flex="300px">
            <RangePicker
              className="admin-filter-picker"
              style={{ width: '100%' }}
              value={
                filters.startDate && filters.endDate
                  ? [dayjs(filters.startDate), dayjs(filters.endDate)]
                  : null
              }
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  applyFilters({
                    startDate: dates[0].toISOString(),
                    endDate: dates[1].toISOString(),
                  });
                } else {
                  applyFilters({ startDate: undefined, endDate: undefined });
                }
              }}
            />
          </Col>
          <Col>
            <Button icon={<FilterOutlined />} onClick={resetFilters} className="admin-filter-btn">
              {t('admin.credit_transactions.btn_reset')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false} className="admin-table-card">
        <Table<CreditTransaction>
          rowKey="id"
          loading={loading}
          dataSource={transactions}
          columns={columns}
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => t('admin.credit_transactions.txn_summary', { count: total }),
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* User Credit Summary Modal */}
      <UserCreditSummaryModal
        userId={selectedUserId}
        visible={summaryVisible}
        onClose={() => {
          setSummaryVisible(false);
          setSelectedUserId(null);
        }}
      />
    </Space>
  );
};

export default CreditTransactions;
